import { useEffect, useMemo } from 'react';
import { File, Folder, User, XCircle } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useData, useMutation } from '../../../Shared/api';
import { containsAddress } from '../../../Shared/helpers';
import { useLocale } from '../../../Shared/locale';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { Campaign, Charity, DonationFields, donorTypes, Gender, Project } from '../../../Shared/types';
import AddressInputs from '../../../Shared/UI/AddressInputs';
import { Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { Checkbox } from '../../../Shared/UI/Checkbox';
import CheckboxLabel from '../../../Shared/UI/CheckboxLabel';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { Icon } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import PhoneNumberInput from '../../../Shared/UI/PhoneNumberInput';
import Picker, { PickerItem } from '../../../Shared/UI/Picker';
import { Radio } from '../../../Shared/UI/Radio';
import RadioLabel from '../../../Shared/UI/RadioLabel';
import { Select } from '../../../Shared/UI/Select';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { Textarea } from '../../../Shared/UI/Textarea';
import { useDateInput } from '../../../Shared/useDateInput';
import { useRouter } from '../../router';
import { Donation, DonationForm } from '../../types';

export default function EditDonationForm({
  donation,
  projects,
  donationForms,
  charity,
}: {
  donation: Donation & DonationFields & {
    campaign: (Campaign & {
      project: Project;
    }) | null;
    donation_form: DonationForm | null;
  };
  projects: PickerItem[];
  donationForms: PickerItem[];
  charity: Charity;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const { defaultCountry, formatDate, parseDate } = useLocale();
  const { handleDateBlur } = useDateInput();
  const notifier = useNotifier();

  const form = useForm({
    defaultValues: {
      project_id: (donation.campaign?.project.id || null) as string | null,
      campaign_id: (donation.campaign?.id || null) as string | null,
      donation_form_id: (donation.donation_form?.id || null) as string | null,
      donor_type: donation.donor_type,
      first_name: donation.first_name,
      last_name: donation.last_name,
      email: donation.email,
      email_opt_in: donation.email_opt_in,
      gender: donation.gender,
      date_of_birth: donation.date_of_birth ? formatDate(donation.date_of_birth, 'input_date') : '',
      company_name: donation.company_name,
      phone: donation.phone,
      phone_opt_in: donation.phone_opt_in,
      hide_name: !donation.publish_name,
      street: donation.street,
      house_number: donation.house_number,
      extra_address_line: donation.extra_address_line,
      zip_code: donation.zip_code,
      city: donation.city,
      country: donation.country || defaultCountry,
      message: donation.message,
    },
  });

  const {
    register, setValue, handleSubmit, setError, watch, control,
    formState: { errors, defaultValues },
  } = form;

  const projectId = watch('project_id');
  const gender = watch('gender');

  const [data] = useData<Campaign[]>(
    projectId ? routes.project_campaigns_json(projectId) : null,
  );

  const campaigns = useMemo(
    () => data || (donation.campaign ? [donation.campaign] : []),
    [data, donation.campaign]
  );

  useEffect(() => {
    if (donation.campaign) {
      if (projectId === donation.campaign.project.id) {
        // Select original campaign after updating options.
        setValue('campaign_id', donation.campaign.id);
      } else {
        // Unset the campaign.
        setValue('campaign_id', null);
      }
    }
  }, [campaigns, projectId, donation]);

  const [editDonation, { loading, errors: serverErrors }] = useMutation(routes['edit_donation'](donation.id), {
    onSuccess: () => {
      notifier.notify(t('backend:donation_saved'));
    },
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  const submit = (data: NonNullable<typeof defaultValues>) => {
    const dateOfBirth = data?.date_of_birth && parseDate(data.date_of_birth, 'input_date');

    editDonation({
      campaign_id: data?.campaign_id,
      donation_form_id: data?.donation_form_id,
      donor_type: data?.donor_type,
      first_name: data?.first_name,
      last_name: data?.last_name,
      email: data?.email,
      email_opt_in: data?.email_opt_in,
      gender: data?.gender,
      date_of_birth: dateOfBirth ? formatDate(dateOfBirth, 'internal_date') : null,
      company_name: data?.company_name,
      phone: data?.phone,
      phone_opt_in: data?.phone_opt_in,
      hide_name: data?.hide_name,
      street: data?.street,
      house_number: data?.house_number,
      extra_address_line: data?.extra_address_line,
      zip_code: data?.zip_code,
      city: data?.city,
      country: data?.country,
      message: data?.message,
      // Unset the prefilled country if no address was entered
      ...!containsAddress(data || {}) && {
        country: null,
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Card panels header footer>
        <div>
          <h3>
            {t('backend:edit_donation_details')}
          </h3>
        </div>
        <div className="space-y-6">
          {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} scrollIntoView />}

          {donation.campaign && (
            <>
              <InputGroup>
                <InputLabel required valid={!errors.project_id} htmlFor="donation_project">
                  {t('backend:project')}
                </InputLabel>
                <Controller
                  control={control}
                  name="project_id"
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Picker
                      selected={value}
                      items={projects}
                      onChange={(value) => onChange(value?.id || null)}
                      onBlur={onBlur}
                      icon={<Folder />}
                      aria-invalid={!!errors.project_id}
                      id="donation_project"
                      disabled={donation.locked_fields?.includes('campaign')}
                    />
                  )}
                />
                <ErrorMessage attribute={t('backend:project')} error={errors.project_id} />
              </InputGroup>

              <InputGroup>
                <InputLabel required valid={!errors.campaign_id} htmlFor="donation_campaign">
                  {t('backend:campaign')}
                </InputLabel>
                <Controller
                  key={projectId}
                  control={control}
                  name="campaign_id"
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Picker
                      selected={value}
                      items={campaigns}
                      onChange={(value) => onChange(value?.id || null)}
                      onBlur={onBlur}
                      icon={<User />}
                      aria-invalid={!!errors.campaign_id}
                      id="donation_campaign"
                      disabled={donation.locked_fields?.includes('campaign')}
                    />
                  )}
                />
                <ErrorMessage attribute={t('backend:campaign')} error={errors.campaign_id} />
              </InputGroup>
            </>
          )}

          {donation.donation_form && (
            <InputGroup>
              <InputLabel required valid={!errors.donation_form_id} htmlFor="donation_donation_form">
                {t('backend:donation_form')}
              </InputLabel>
              <Controller
                control={control}
                name="donation_form_id"
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Picker
                    selected={value}
                    items={donationForms}
                    onChange={(value) => onChange(value?.id || null)}
                    onBlur={onBlur}
                    icon={<File />}
                    aria-invalid={!!errors.donation_form_id}
                    id="donation_donation_form"
                    disabled={donation.locked_fields?.includes('campaign')}
                  />
                )}
              />
              <ErrorMessage attribute={t('backend:donation_form')} error={errors.donation_form_id} />
            </InputGroup>
          )}

          <InputGroup>
            <InputLabel required valid={!errors.donor_type} htmlFor="edit_donation_donor_type">
              {t('backend:donor_type')}
            </InputLabel>
            <Select
              {...register('donor_type', { required: true })}
              id="edit_donation_donor_type"
              aria-invalid={!!errors.donor_type}
            >
              {donorTypes.map((donorType) => (
                <option value={donorType} key={donorType}>
                  {t(`backend:donor_types.${donorType}`)}
                </option>
              ))}
            </Select>
            <ErrorMessage attribute={t('backend:donor_type')} error={errors.donor_type }/>
          </InputGroup>

          <div className="grid grid-cols-2 gap-6">
            <InputGroup>
              <InputLabel valid={!errors.first_name} htmlFor="donation_first_name">
                {t('backend:first_name')}
              </InputLabel>
              <Input
                {...register('first_name')}
                id="donation_first_name"
                className="mr-4"
                aria-invalid={!!errors.first_name}
              />
              <ErrorMessage attribute={t('backend:first_name')} error={errors.first_name }/>
            </InputGroup>

            <InputGroup>
              <InputLabel valid={!errors.last_name} htmlFor="donation_last_name">
                {t('backend:last_name')}
              </InputLabel>
              <Input
                {...register('last_name')}
                id="donation_last_name"
                className="mr-4"
                aria-invalid={!!errors.last_name}
              />
              <ErrorMessage attribute={t('backend:last_name')} error={errors.last_name }/>
            </InputGroup>
          </div>

          <InputGroup>
            <InputLabel valid={!errors.email} htmlFor="donation_email">
              {t('backend:email_address')}
            </InputLabel>
            <Input
              {...register('email')}
              inputMode="email"
              id="donation_email"
              aria-invalid={!!errors.email}
            />
            <ErrorMessage attribute={t('backend:email_address')} error={errors.email }/>
          </InputGroup>

          <InputGroup>
            <InputLabel valid={!errors.gender} htmlFor="donation_gender">
              {t('backend:gender')}
            </InputLabel>
            <div className="flex space-x-3">
              <RadioLabel>
                <Radio
                  {...register('gender')}
                  value={Gender.Male}
                  aria-invalid={!!errors.gender}
                />
                <span>
                  {t('shared:genders.male')}
                </span>
              </RadioLabel>
              <RadioLabel>
                <Radio
                  {...register('gender')}
                  value={Gender.Female}
                  aria-invalid={!!errors.gender}
                />
                <span>
                  {t('shared:genders.female')}
                </span>
              </RadioLabel>
              <RadioLabel>
                <Radio
                  {...register('gender')}
                  value={Gender.Nonbinary}
                  aria-invalid={!!errors.gender}
                />
                <span>
                  {t('shared:genders.nonbinary')}
                </span>
              </RadioLabel>
              {gender && (
                <Button
                  onClick={(event) => {
                    event.preventDefault(); setValue('gender', null);
                  }}
                  variant="tertiary"
                  size="xs"
                  aria-label={t('shared:filter.clear')}
                >
                  <Icon>
                    <XCircle />
                  </Icon>
                </Button>
              )}
            </div>
            <ErrorMessage error={errors.gender} attribute={t('frontend:gender')} />
          </InputGroup>

          <InputGroup>
            <InputLabel htmlFor="donation_date_of_birth">
              {t('backend:date_of_birth')}
            </InputLabel>
            <Input
              {...register('date_of_birth')}
              onBlur={(event) => {
                handleDateBlur(event);
                setValue('date_of_birth', event.target.value);
              }}
              id="donation_date_of_birth"
              placeholder={t('shared:date_input.date_format_placeholder')}
              aria-invalid={!!errors.date_of_birth}
            />
            <ErrorMessage error={errors.date_of_birth} attribute={t('backend:date_of_birth')} />
          </InputGroup>

          <InputGroup>
            <InputLabel valid={!errors.company_name} htmlFor="donation_company_name">
              {t('backend:company_name')}
            </InputLabel>
            <Input
              {...register('company_name')}
              id="donation_company_name"
              aria-invalid={!!errors.company_name}
            />
            <ErrorMessage error={errors.company_name} attribute={t('backend:company_name')} />
          </InputGroup>

          <InputGroup>
            <InputLabel htmlFor="donation_phone">
              {t('backend:phone_number')}
            </InputLabel>
            <Controller
              control={control}
              name="phone"
              render={({ field: { onChange, onBlur, value } }) => (
                <PhoneNumberInput
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  country={donation.country}
                  id="donation_phone"
                  ariaInvalid={!!errors.phone}
                />
              )}
            />
            <ErrorMessage error={errors.phone} attribute={t('backend:phone_number')} />
          </InputGroup>

          <AddressInputs form={form} required={false} />

          {donation.campaign && (
            <InputGroup>
              <InputLabel htmlFor="donation_message">
                {t('backend:message')}
              </InputLabel>
              <Textarea
                {...register('message')}
                id="donation_message"
              />
              <ErrorMessage error={errors.message} attribute={t('backend:message')} />
            </InputGroup>
          )}

          <InputGroup className="space-y-2">
            {donation.campaign && (
              <CheckboxLabel>
                <Checkbox
                  {...register('hide_name')}
                  id="donation_hide_name"
                  className="mt-1"
                />
                <span>
                  {t('backend:hide_name_text_company')}
                </span>
              </CheckboxLabel>
            )}

            <CheckboxLabel htmlFor="donation_email_opt_in">
              <Checkbox
                {...register('email_opt_in')}
                id="donation_email_opt_in"
                className="mt-1"
              />
              <span>
                {t('backend:email_opt_in_text', { charity: charity.title })}
              </span>
            </CheckboxLabel>

            <CheckboxLabel htmlFor="donation_phone_opt_in">
              <Checkbox
                {...register('phone_opt_in')}
                id="donation_phone_opt_in"
                className="mt-1"
              />
              <span>
                {t('backend:phone_opt_in_text', { charity: charity.title })}
              </span>
            </CheckboxLabel>
          </InputGroup>
        </div>
        <div className="text-right">
          <Button
            type="submit"
            loading={loading}
            className="ml-auto"
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>
    </form>
  );
}
